"use client";

import { IconExternalLink, IconInfoCircle, IconList, IconSearch } from "@tabler/icons-react";
import Link from "next/link";
import { useMemo } from "react";
import { KeyValue } from "@/components/domain/key-value";
import { KeyValueGrid } from "@/components/domain/key-value-grid";
import { ReportSection } from "@/components/domain/report-section";
import { sections } from "@/lib/constants/sections";
import { IMPORTANT_HEADERS } from "@domainstack/constants";
import type { HeadersResponse } from "@domainstack/types";
import { Alert, AlertDescription } from "@domainstack/ui/alert";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@domainstack/ui/empty";
import { normalizeDomainInput } from "@domainstack/utils/domain/client";

/**
 * Extract domain from a Location header value.
 * Location can be a full URL or a relative path.
 */
function extractDomainFromLocation(locationValue: string): string | null {
  try {
    // Try to parse as URL
    const url = new URL(locationValue);
    return normalizeDomainInput(url.hostname);
  } catch {
    // If it fails, it might be a relative URL, return null
    return null;
  }
}
export function HeadersSection({
  data
}: {
  domain?: string;
  data?: HeadersResponse | null;
}) {
  const status = data?.status;
  const statusMessage = data?.statusMessage;

  // Sort headers client-side: important headers first, then alphabetically
  const headers = useMemo(() => {
    const filtered = data?.headers?.filter(h => h.value.trim() !== "") ?? [];
    return filtered.sort((a, b) => Number(IMPORTANT_HEADERS.has(b.name?.toLowerCase() ?? "")) - Number(IMPORTANT_HEADERS.has(a.name?.toLowerCase() ?? "")) || (a.name?.toLowerCase() ?? "").localeCompare(b.name?.toLowerCase() ?? "", "en"));
  }, [data?.headers]);
  if (status === 0 && statusMessage === "Invalid SSL certificate") {
    return null;
  }
  return <ReportSection {...sections.headers}>
      {headers && headers.length > 0 ? <div className="space-y-4">
          {status !== 200 && <Alert>
              <IconInfoCircle aria-hidden />
              <AlertDescription>
                <p className="text-[13px]">
                  Server returned{" "}
                  <a href={`https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Status/${status}`} target="_blank" rel="noopener" className="inline-flex items-center gap-1 text-foreground/90 underline underline-offset-3 hover:text-muted-foreground">
                    <span className="font-medium">
                      {status}
                      {statusMessage ? ` ${statusMessage}` : ""}
                    </span>
                    <IconExternalLink className="!size-3.5" aria-hidden />
                  </a>
                  {"."}
                </p>
              </AlertDescription>
            </Alert>}

          <KeyValueGrid colsDesktop={2}>
            {headers.map(h_0 => {
          const isLocation = h_0.name?.toLowerCase() === "location";
          const locationDomain = isLocation ? extractDomainFromLocation(h_0.value) : null;
          return <KeyValue key={`header-${h_0.name}-${h_0.value}`} label={h_0.name} value={h_0.value} copyable highlight={IMPORTANT_HEADERS.has(h_0.name?.toLowerCase() ?? "")} suffix={locationDomain ? <Link href={`/${encodeURIComponent(locationDomain)}`} className="inline-flex items-center text-foreground/80 hover:text-muted-foreground" title={`View report for ${locationDomain}`}>
                        <IconSearch className="h-4 w-4" />
                      </Link> : undefined} />;
        })}
          </KeyValueGrid>
        </div> : <div className="space-y-4">
          {status === 0 && statusMessage ? <Alert variant="destructive">
              <IconInfoCircle className="h-4 w-4" />
              <AlertDescription>{statusMessage}</AlertDescription>
            </Alert> : null}
          <Empty className="border border-solid bg-background/60">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <IconList />
              </EmptyMedia>
              <EmptyTitle>No HTTP headers detected</EmptyTitle>
              <EmptyDescription>
                We couldn&apos;t fetch any HTTP response headers for this site. It may be offline or
                blocking requests.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>}
    </ReportSection>;
}